import React, { useState } from "react";
import { Button, Input, Form, message } from "antd";
import { useAuth } from "../../hooks/useAuth";
import { useUpdateUser } from "../../hooks/useUsers";
import { useQuery } from "@tanstack/react-query";
import apiClient from "../../api/apiClient";

const SecuritySection: React.FC = () => {
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [form] = Form.useForm();

  // Get current user ID from auth hook
  const { data: authUser } = useAuth();

  // Fetch detailed user data
  const { data: userData } = useQuery({
    queryKey: ["user", authUser?.id],
    queryFn: async () => {
      if (!authUser?.id) return null;
      const response = await apiClient.get(`/user/${authUser.id}`);
      return response.data.data;
    },
    enabled: !!authUser?.id,
  });

  // Update user mutation
  const updateUserMutation = useUpdateUser();

  const toggleChangePassword = () => {
    setIsChangingPassword(!isChangingPassword);
    if (!isChangingPassword) {
      form.resetFields();
    }
  };

  const onFinish = async (values: {
    password: string;
    confirm_password: string;
  }) => {
    if (!authUser?.id || !userData) {
      message.error("User data not available");
      return;
    }

    try {
      // Create user update payload with password
      const userUpdateData = {
        // Include existing user data
        name: userData.name,
        email: userData.email,
        phone: userData.phone || "",
        user_type_id: userData.user_type_id || 0,
        role_id: userData.roles?.id,
        location_id: userData.location_id || 0,
        gender: userData.gender || "male",
        country: userData.country || "",
        state: userData.state || "",
        lga: userData.lga || "",
        status: true,
        // Add the new password
        password: values.password,
      };

      // Call update user mutation
      await updateUserMutation.mutateAsync({
        userId: authUser.id,
        userData: userUpdateData,
      });

      message.success("Password updated successfully");
      setIsChangingPassword(false);
      form.resetFields();
    } catch (error: any) {
      console.error("Password update error:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to update password";
      message.error(errorMessage);
    }
  };

  const validateConfirmPassword = (_: any, value: string) => {
    const password = form.getFieldValue("password");
    if (!value || password === value) {
      return Promise.resolve();
    }
    return Promise.reject(new Error("The two passwords do not match"));
  };

  return (
    <div className="p-6 bg-white rounded-lg border border-gray-200">
      <h2 className="text-xl font-bold mb-4">Security Settings</h2>

      {!isChangingPassword ? (
        <Button
          onClick={toggleChangePassword}
          className="border border-gray-300 hover:border-gray-400 px-4 py-2 rounded-lg text-gray-700"
        >
          Change Password
        </Button>
      ) : (
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="password"
            label="New Password"
            rules={[
              { required: true, message: "Please enter your new password" },
              { min: 8, message: "Password must be at least 8 characters" },
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            name="confirm_password"
            label="Confirm New Password"
            dependencies={["password"]}
            rules={[
              { required: true, message: "Please confirm your password" },
              { validator: validateConfirmPassword },
            ]}
          >
            <Input.Password />
          </Form.Item>

          <div className="flex space-x-2">
            <Button
              onClick={toggleChangePassword}
              className="px-4 py-2"
              disabled={updateUserMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={updateUserMutation.isPending}
              className="bg-blue-600 text-white px-4 py-2 hover:bg-blue-700"
            >
              Save
            </Button>
          </div>
        </Form>
      )}
    </div>
  );
};

export default SecuritySection;
